import React, { memo, useMemo, useEffect, useState } from "react";
import { valueFormat } from "src/helper/valueFormat";
import { checkDiscount } from "src/helper/checkDiscount";
import {
  convertStaffTip,
  convertTaxAndFee,
  convertTips,
} from "src/helper/tips";
import { MESSAGE_VARIABLES } from "src/constants";
import {
  buildCouponLines,
  buildPromotionLines,
  buildReceiptDiscountLines,
} from "src/helper/checkOrderSummary";

const sumAmounts = (rows) =>
  (rows || []).reduce((sum, row) => sum + (Number(row.amount) || 0), 0);

const OrderCharges = ({ orderData, itemPromotions }) => {
  const [maxLength, setMaxLength] = useState(20);
  const stageCouponTotal =
    sumAmounts(itemPromotions?.couponRows) ||
    Number(orderData?.charges?.couponAmount) ||
    0;
  const stagePromotionTotal = sumAmounts(itemPromotions?.promotionRows);
  const checkTipSettings = useMemo(() => {
    const currentBusiness = orderData?.business[0];
    const { tipRestaurant, tipDriver } =
      currentBusiness?.settings?.website?.tipDisplaySetting;

    return {
      tipRestaurantEnabled: tipRestaurant?.showTipRestaurant,
      tipDriverEnabled: tipDriver?.showTipDriver,
    };
  }, [orderData]);

  useEffect(() => {
    const handleResize = () => {
      const isSmallScreen = window.innerWidth <= 420;
      setMaxLength(isSmallScreen ? 10 : 20);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const getPromotionInfoText = (promotionInfo, length = 20) => {
    if (!promotionInfo) return "";

    const text = promotionInfo.couponTitle
      ? `${promotionInfo.couponTitle}`
      : promotionInfo.couponName
        ? `${promotionInfo.couponName}`
        : "";

    return text.length > length
      ? `(${text.substring(0, length)}...)`
      : text
        ? `(${text})`
        : "";
  };

  const checkSummary = useMemo(() => {
    const details = orderData?.charges?.discountDetails;
    const discountLines = buildReceiptDiscountLines(details);
    const couponLines = buildCouponLines(details);
    const promotionLines = buildPromotionLines(
      orderData?.charges?.promotionDetails,
    );
    const discountTotal = discountLines.reduce(
      (sum, line) => sum + line.amount,
      0,
    );
    const promotionFromDetails = promotionLines.reduce(
      (sum, line) => sum + line.amount,
      0,
    );
    const promotionTotal =
      Math.abs(Number(orderData?.charges?.promotionDiscount) || 0) ||
      promotionFromDetails;
    return {
      discountLines,
      couponLines,
      promotionLines,
      discountTotal,
      promotionTotal,
    };
  }, [orderData?.charges]);

  const hasStructuredAdjustments =
    checkSummary.discountLines.length > 0 ||
    checkSummary.couponLines.length > 0 ||
    checkSummary.promotionLines.length > 0 ||
    checkSummary.promotionTotal > 0;

  return (
    <>
      <tr className="total" style={{ fontSize: "16px" }}>
        <td>Subtotal</td>
        <td>
          {valueFormat(`$${Number(orderData?.charges?.subTotal).toFixed(2)}`)}
        </td>
      </tr>
      {hasStructuredAdjustments ? (
        <>
          {checkSummary.promotionTotal > 0 ? (
            <tr className="product">
              <td>Promotions</td>
              <td style={{ fontSize: "14px" }}>
                {valueFormat(
                  `($${Number(checkSummary.promotionTotal).toFixed(2)})`,
                )}
              </td>
            </tr>
          ) : null}
          {checkSummary.promotionLines.map((line) => (
            <tr
              className="product"
              key={line.key}
              style={{ paddingLeft: "32px" }}
            >
              <td>{line.label}</td>
              <td style={{ fontSize: "14px" }}>
                {valueFormat(`($${Number(line.amount).toFixed(2)})`)}
              </td>
            </tr>
          ))}
          {checkSummary.discountTotal > 0 ? (
            <tr className="product">
              <td>Discounts</td>
              <td style={{ fontSize: "14px" }}>
                {valueFormat(
                  `($${Number(checkSummary.discountTotal).toFixed(2)})`,
                )}
              </td>
            </tr>
          ) : null}
          {checkSummary.discountLines.map((line) => (
            <tr
              className="product"
              key={line.label}
              style={{ paddingLeft: "32px" }}
            >
              <td>{line.label}</td>
              <td style={{ fontSize: "14px" }}>
                {valueFormat(`($${Number(line.amount).toFixed(2)})`)}
              </td>
            </tr>
          ))}
          {checkSummary.couponLines.map((line) => (
            <tr className="product" key={line.key}>
              <td>{line.label}</td>
              <td style={{ fontSize: "14px" }}>
                {valueFormat(`($${Number(line.amount).toFixed(2)})`)}
              </td>
            </tr>
          ))}
        </>
      ) : (
        <>
          {stageCouponTotal > 0 && (
            <tr className="product">
              <td>
                Coupon{" "}
                {getPromotionInfoText(orderData?.promotionInfo, maxLength)}
              </td>
              <td style={{ fontSize: "14px" }}>
                {valueFormat(`($${Number(stageCouponTotal).toFixed(2)})`)}
              </td>
            </tr>
          )}
          {stagePromotionTotal > 0 && (
            <tr className="product">
              <td>Promotion</td>
              <td style={{ fontSize: "14px" }}>
                {valueFormat(`($${Number(stagePromotionTotal).toFixed(2)})`)}
              </td>
            </tr>
          )}
        </>
      )}
      {orderData?.charges?.discount && orderData?.charges?.discount > 0 ? (
        <tr className="product">
          <td>{MESSAGE_VARIABLES.cashback_used}</td>
          <td style={{ fontSize: "14px" }}>
            {checkDiscount(orderData?.charges?.discount)}
          </td>
        </tr>
      ) : (
        <></>
      )}
      {(checkTipSettings.tipDriverEnabled ||
        checkTipSettings.tipRestaurantEnabled) && (
        <>
          {orderData.type !== "delivery" &&
          !checkTipSettings.tipRestaurantEnabled ? (
            <></>
          ) : (
            <tr className="product">
              <td>Tips</td>
              <td style={{ fontSize: "14px" }}>
                {convertTips(orderData?.charges?.tipInfo, orderData)}
              </td>
            </tr>
          )}
          {(orderData.charges?.tipInfo?.kitchen ||
            orderData.charges?.tipInfo?.kitchen == 0) &&
          checkTipSettings.tipRestaurantEnabled ? (
            <tr className="tip" style={{ paddingLeft: "10px" }}>
              <td>Staff</td>
              <td style={{ paddingRight: "70px", fontSize: "14px" }}>
                {convertStaffTip(orderData.charges?.tipInfo?.kitchen)}
              </td>
            </tr>
          ) : (
            ""
          )}
          {orderData.type === "delivery" &&
          checkTipSettings.tipDriverEnabled ? (
            <>
              {orderData.charges?.tipInfo?.driver ||
              orderData.charges?.tipInfo?.driver === 0 ? (
                <tr className="tip" style={{ paddingLeft: "10px" }}>
                  <td>Driver</td>
                  <td style={{ paddingRight: "70px", fontSize: "14px" }}>
                    {convertStaffTip(orderData.charges?.tipInfo?.driver)}
                  </td>
                </tr>
              ) : (
                ""
              )}
            </>
          ) : (
            ""
          )}
        </>
      )}
      <tr className="product">
        <td>Taxes and Fees</td>
        <td style={{ fontSize: "14px" }}>
          {convertTaxAndFee(
            orderData?.charges?.servicingFee,
            orderData?.charges?.tax,
          )}
        </td>
      </tr>
      <tr className="product" style={{ paddingLeft: "10px" }}>
        <td>Tax</td>
        <td style={{ paddingRight: "70px", fontSize: "14px" }}>
          {valueFormat(`$${Number(orderData?.charges?.tax).toFixed(2)}`)}
        </td>
      </tr>
      {orderData?.charges?.servicingFee?.processingFee ||
      orderData?.charges?.servicingFee?.marketplaceFee ||
      orderData?.charges?.servicingFee?.ordersCoFee ? (
        <tr className="product" style={{ paddingLeft: "10px" }}>
          <td>Processing Fee</td>
          <td style={{ paddingRight: "70px", fontSize: "14px" }}>
            {valueFormat(
              `$${Number(
                orderData?.charges?.servicingFee?.processingFee +
                  orderData?.charges?.servicingFee?.marketplaceFee +
                  orderData?.charges?.servicingFee?.ordersCoFee,
              ).toFixed(2)}`,
            )}
          </td>
        </tr>
      ) : (
        ""
      )}
      {orderData.type === "delivery" && (
        <tr className="product">
          <td>Delivery Fee</td>
          <td style={{ fontSize: "14px" }}>
            {valueFormat(
              `$${Number(orderData?.charges?.deliveryFee).toFixed(2)}`,
            )}
          </td>
        </tr>
      )}
      <tr>
        <td style={{ flex: 1, padding: 0 }}>
          <hr className="line-header" />
        </td>
      </tr>
      <tr className="product-total">
        <td>Total</td>
        <td>
          {valueFormat(`$${Number(orderData?.charges?.total).toFixed(2)}`)}
        </td>
      </tr>
    </>
  );
};
export default memo(OrderCharges);
